import * as Dialog from '@radix-ui/react-dialog';
import CloseIcon from '../../assets/icons/close.svg?react';
import { Button } from '../Button';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isNegative?: boolean;
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isNegative = false,
  loading = false,
}: ConfirmDialogProps) {
  const handleConfirm = async () => {
    await onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className='data-[state=open]:animate-in
            data-[state=closed]:animate-out data-[state=closed]:fade-out-0
            data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50'
        />
        <Dialog.Content
          className='data-[state=open]:animate-in
            data-[state=closed]:animate-out data-[state=closed]:fade-out-0
            data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95
            data-[state=open]:zoom-in-95
            data-[state=closed]:slide-out-to-left-1/2
            data-[state=closed]:slide-out-to-top-[48%]
            data-[state=open]:slide-in-from-left-1/2
            data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%]
            z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%]
            rounded-lg bg-white p-6 shadow-lg duration-200'
          aria-describedby='confirm-dialog-description'
        >
          {/* Header */}
          <div className='mb-4 flex items-start justify-between'>
            <Dialog.Title className='size-h3 text-focus font-semibold'>
              {title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className='text-focus hover:text-focus-hover rounded-sm
                  opacity-70 transition-opacity hover:opacity-100 focus:ring-2
                  focus:ring-gray-400 focus:ring-offset-2 focus:outline-none
                  disabled:pointer-events-none'
                aria-label='Close'
              >
                <CloseIcon className='h-4 w-4' />
              </button>
            </Dialog.Close>
          </div>

          {/* Description */}
          <div id='confirm-dialog-description' className='mb-6'>
            <p className='size-p1 text-gray-700'>{description}</p>
          </div>

          {/* Actions */}
          <div className='flex justify-end gap-3'>
            <Dialog.Close asChild>
              <Button
                variant='secondary'
                state={loading ? 'disabled' : 'default'}
              >
                {cancelLabel}
              </Button>
            </Dialog.Close>
            <Button
              variant='primary'
              state={loading ? 'disabled' : isNegative ? 'negative' : 'default'}
              onClick={handleConfirm}
            >
              {loading ? 'Processing...' : confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
